// src/com/arrive_info/ArriveInfo.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
// import { styled } from '@mui/material/styles';
import { Button, IconButton, Popover } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'; // 사용
import StopCircleIcon from '@mui/icons-material/StopCircle'; // 사용중지.
import StoreIcon from '@mui/icons-material/Store'; // 점포목록
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'; // AUTO 우선순위 그룹
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion'; // 자동배차 처리 표시
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // 도착정보 업로드
import PaidIcon from '@mui/icons-material/Paid'; // 운임정보 Popup.
import PageviewIcon from '@mui/icons-material/Pageview';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import { CircleLoading } from '../../common/CircleLoading';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import FlexyTable from '../../common/flexytable/FlexyTable';
import VzmSelector from '../../common/VzmSelector';
import VzmCheckbox from '../../common/VzmCheckbox';
import { AppObject, AppStyle, AppWord, CodeGroup } from '../../value/AppConst';
import {
    LIST_ARRIVE_INFO, GET_ARRIVE_INFO, ADD_ARRIVE_INFO, EDIT_ARRIVE_INFO, REMOVE_ARRIVE_INFO,
    SET_USAGE_ARRIVE_INFO, SET_AUTO_PLAN_ORDER_ARRIVE_INFO, UNSET_AUTO_PLAN_ORDER_ARRIVE_INFO,
    SET_AUTO_VIEW_ORDER_ARRIVE_INFO
} from './ArriveInfoGql';
import { ArriveInfoDictionary } from './ArriveInfoDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import ArriveInfoView from './ArriveInfoView';
import ArriveInfoInput from './ArriveInfoInput';
import {
    arriveInfoListRepo, userInfoRepo, dcInfoListRepo,
    codeTreeRepo, codeNameRepo, dcSelectedRepo, authTableTreeRepo
} from '../../value/AppModel';
import { LIST_DC_INFO } from '../dc_info/DcInfoGql';
import ValueMisc from '../../value/ValueMisc';
import StoreInfo from '../store_info/StoreInfo';
import ArriveAutoGroup from './ArriveAutoGroup';
import ArriveAutoViewOrder from './ArriveAutoViewOrder';
import ArriveInfoUploader from './ArriveInfoUploader';
import ArriveChargeInfo from '../arrive_charge_info/ArriveChargeInfo';

const AutoPlanFilterSelect = [
    {value:AppWord.ALL, label:AppWord.AllKorean},
    {value:'Y', label:'사용'},
    {value:'N', label:'사용 안함'},
];

const UsageFilterSelect = [
    {value:AppWord.ALL, label:AppWord.AllKorean},
    {value:'Y', label:'사용'},
    {value:'N', label:'사용 중지'},
];

/**
 * (ARRIVE_INFO) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-08-29 09:56:09 using DbdbTap
 */
export default function ArriveInfo({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const arriveInfoListData = useReactiveVar(arriveInfoListRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const codeNameDict = useReactiveVar(codeNameRepo);
    const [viewerState, setViewerState] = useState(AppObject.EmptyViewerState);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const [openStoreList, setOpenStoreList] = useState(false);
    const [autoOrderGroupEditorState, setAutoOrderGroupEditorState] = useState({open:false, planGroup:[]});
    const [autoOrderControlOpen, setAutoOrderControlOpen] = useState(false);
    const [uploadViewState, setUploadViewState] = useState({open:false, csvText:''});
    const [arriveListForDc, setArriveListForDc] = useState([]); // filtered list
    const dcInfoListData = useReactiveVar(dcInfoListRepo); // for selection.
    // const [dcSelected, setDcSelected] = useState({}); // dcCd, dcName
    const dcSelected = useReactiveVar(dcSelectedRepo);
    const [filterAutoPlan, setFilterAutoPlan] = useState(AutoPlanFilterSelect[0].value);
    const [filterUsage, setFilterUsage] = useState(UsageFilterSelect[0].value);
    const [searchText, setSearchText] = useState('');
    const [chargeInfoState, setChargeInfoState] = useState({open:false, item:null});
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const checkAuth = useAuthorizationCheck();
    const { enqueueSnackbar } = useSnackbar();
    const [useEucKr, setUseEucKr] = useState(false);
    const [uploadPopAnchorEl, setUploadPopAnchorEl] = useState(null);
    const fileInputRef = useRef(null);
    const loadRef = useRef(0);
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    const [getDcInfoList, responseDcList] = useLazyQuery(LIST_DC_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetDcList(data, option)},
		onError: (error) => addAlert({title: '출발지 목록 조회 오류', message: [error.message]})
    });

    const [getArriveInfoList, responseList] = useLazyQuery(LIST_ARRIVE_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: ArriveInfoDictionary.errorTitle.Get, message: [error.message]})
    });
    const [getArriveInfoItem, responseItem] = useLazyQuery(GET_ARRIVE_INFO, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: ArriveInfoDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addArriveInfo, responseAdd] = useMutation( ADD_ARRIVE_INFO, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: ArriveInfoDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editArriveInfo, responseEdit] = useMutation( EDIT_ARRIVE_INFO, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: ArriveInfoDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeArriveInfo, responseRemove] = useMutation( REMOVE_ARRIVE_INFO, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: ArriveInfoDictionary.errorTitle.Remove, message: [error.message]})
	});

    const [setUsageArriveInfo, responseUsage] = useMutation( SET_USAGE_ARRIVE_INFO, {
        onCompleted: (data, option) => onComplateSetUsage(data, option),
        onError: (error) => addAlert({title: "출발도착정보 사용여부 변경 오류", message: [error.message]})
    });
    const [setAutoPlanOrderArriveInfo, responseSetAutoPlanOrder] = useMutation( SET_AUTO_PLAN_ORDER_ARRIVE_INFO, {
        onCompleted: (data, option) => onComplateSetAutoPlanOrder(data, option),
        onError: (error) => addAlert({title: "AUTO 우선순위 그룹 추가 오류", message: [error.message]})
    });
    const [unsetAutoPlanOrderArriveInfo, responseUnsetAutoPlanOrder] = useMutation( UNSET_AUTO_PLAN_ORDER_ARRIVE_INFO, {
        onCompleted: (data, option) => onComplateUnsetAutoPlanOrder(data, option),
        onError: (error) => addAlert({title: "AUTO 우선순위 그룹 제외 오류", message: [error.message]})
    });
    const [setAutoViewOrderArriveInfo, responseSetAutoViewOrder] = useMutation( SET_AUTO_VIEW_ORDER_ARRIVE_INFO, {
        onCompleted: (data, option) => onComplateSetAutoViewOrder(data, option),
        onError: (error) => addAlert({title: "자동배차 처리 표시 순서 변경 오류", message: [error.message]})
    });

    const getArriveInfoListNow = () => {
        //const param = {variables:{arriveId:record.arriveId}};
        //getArriveInfoList(param);
        getArriveInfoList(); // Get every records. useEffect will filter with dcCd.
        lastReloadRef.current = new Date().getTime()/1000;
    };

    // load dcInfoList and arriveInfoList
    useEffect(() => {
        if(loadRef.current === 0) {
            loadRef.current = 1;
            let diff = new Date().getTime()/1000 - arriveInfoListData.when;
            if(diff > 300) getArriveInfoListNow();
            diff = new Date().getTime()/1000 - dcInfoListData.when;
            if(diff > 600 || dcInfoListData.records.length === 0) getDcInfoList();
        }
    }, []);

    // select dcCd
    useEffect(()=>{
        if(dcInfoListData.records.length > 0 && !dcSelected.dcCd) {
            // setDcSelected(dcInfoListData.records[0]);
            const dcRec = dcInfoListData.records.find((item)=>item.dcCd === sessionInfo.user.dscIdAs);
            dcSelectedRepo(dcRec);
        }
    }, [dcInfoListData, dcSelected, sessionInfo]);

    // filter arriveInfoList with dcCd
    useEffect(()=>{
        if(dcSelected.dcCd) {
            const list = arriveInfoListData.records;
            if(list.length > 0) {
                // filter, then sort using storeName
                setArriveListForDc(list.filter((item)=>{
                    if(item.dcCd === dcSelected.dcCd) {
                        if(filterAutoPlan !== AppWord.ALL && item.autoPlanYn !== filterAutoPlan) return false;
                        if(filterUsage !== AppWord.ALL && item.useYn !== filterUsage) return false;
                        return true;
                    }
                    return false;
                }).sort((a,b)=>{
                    if(a.storeName > b.storeName) return 1;
                    if(a.storeName < b.storeName) return -1;
                    return 0;
                }));
            }
        }
    }, [dcSelected, arriveInfoListData, filterAutoPlan, filterUsage]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetDcList = (data, clientOption) => {
        if(data.dcInfoList) {
            dcInfoListRepo({when:new Date().getTime()/1000, records:data.dcInfoList});
        }
    };

    const onCompleteGetList = (data, clientOption) => {
        if(data.arriveInfoList) {
            arriveInfoListRepo({when:new Date().getTime()/1000, records:data.arriveInfoList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.arriveInfoItem) {
            if(isEditingRef.current) setEditorState({open:true, item:data.arriveInfoItem});
            else setViewerState({open:true, item:data.arriveInfoItem});
        }
        else addAlert({title:ArriveInfoDictionary.errorTitle.Get, message:['지정한 출발도착정보를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.arriveInfoAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getArriveInfoListNow();
        }
        else addAlert({title:ArriveInfoDictionary.errorTitle.Add, message:[data.arriveInfoAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.arriveInfoEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getArriveInfoListNow();
        }
        else addAlert({title:ArriveInfoDictionary.errorTitle.Edit, message:[data.arriveInfoEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.arriveInfoRemove.ok) getArriveInfoListNow();
        else addAlert({title:ArriveInfoDictionary.errorTitle.Remove, message:[data.arriveInfoRemove.message]});
    };

    const onComplateSetUsage = (data, option) => {
        if(data.arriveInfoSetUsage.ok) getArriveInfoListNow();
        else addAlert({title: "출발도착정보 사용여부 변경 오류", message: [data.arriveInfoSetUsage.message]});
    };

    const onComplateSetAutoPlanOrder = (data, option) => {
        if(data.arriveInfoSetAutoPlanOrder.ok) getArriveInfoListNow();
        else addAlert({title: "AUTO 우선순위 그룹 추가 오류", message: [data.arriveInfoSetAutoPlanOrder.message]});
    };

    const onComplateUnsetAutoPlanOrder = (data, option) => {
        if(data.arriveInfoUnsetAutoPlanOrder.ok) getArriveInfoListNow();
        else addAlert({title: "AUTO 우선순위 그룹 제외 오류", message: [data.arriveInfoUnsetAutoPlanOrder.message]});
    };

    const onComplateSetAutoViewOrder = (data, option) => {
        if(data.arriveInfoSetAutoViewOrder.ok) getArriveInfoListNow();
        else addAlert({title: "자동배차 처리 표시 순서 변경 오류", message: [data.arriveInfoSetAutoViewOrder.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 60) {
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else getArriveInfoListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{arriveInfo:item}};
        if(isEdit) editArriveInfo(mutateParam);
        else addArriveInfo(mutateParam);
    };

    const onCsvFileRead = (event) => {
        const content = event.target.result;
        setUploadViewState({open:true, csvText:content});
    };

    const onChooseFile = (event) => {
        if(event.target.files.length > 0) {
            const file = event.target.files[0];
    
            if (file) {
                const reader = new FileReader();
                reader.onload = onCsvFileRead;
                if(useEucKr) reader.readAsText(file, 'euc-kr');
                else reader.readAsText(file); // utf-8
            }
        }
    };

    const onClickUpload = () => {
        if(fileInputRef.current) {
            setUploadPopAnchorEl(null);
            fileInputRef.current.value='';
            fileInputRef.current.click();
        }
    };

    const onCloseUploader = (countGood) => {
        setUploadViewState({open:false, csvText:''});
        if(countGood>0) getArriveInfoListNow();
    };

    const onClickOpenAutoOrderGroupEditor = () => {
        // check codeTree first.
        const planGroup = ValueMisc.getCodes(codeTree, CodeGroup.AUTO_PLAN_ORDER_GROUP);

        if(!planGroup) {
            addAlert({title: "코드정보 오류", message: [
                "해당 업무를 진행하기 위한 코드를 찾을 수 없습니다.",
                "관리자에게 문의하세요."]});
            return;
        }
        const groupForDc
            = planGroup.filter((c)=>c.code1 === dcSelected.dcTypeCd)
                .map((c) => {return {value:parseInt(c.codeId), label:c.codeName}});
        if(groupForDc.length === 0) {
            const dcTypeName = codeNameDict.DC_TYPE_CD[dcSelected.dcTypeCd];
            addAlert({title: "AUTO 우선순위 그룹 무관", message: [
                dcTypeName + " 타입의 DC는 AUTO 우선순위 그룹을 사용하지 않습니다."
            ]});
            return;
        }

        setAutoOrderGroupEditorState({open:true, planGroup:groupForDc});
    };

    const onClickOpenAutoOrderSetter = () => {
        // UPDATE ARRIVE_INFO SET AUTO_VIEW_ORDER=xxxx
        setAutoOrderControlOpen(true);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getArriveInfoItem({variables:{arriveId:item.arriveId}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "출발도착정보 삭제",
            content: [
                `${item.dcName}-${item.storeName}간 출발도착정보를 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeArriveInfo({variables:{arriveInfo:{arriveId:item.arriveId}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        isEditingRef.current = false;
        getArriveInfoItem({variables:{arriveId:record.arriveId}});
    };

    const onCloseView = () => {
        setViewerState(AppObject.EmptyViewerState);
    };

    const onRequestEditFromViewer = (item) => {
        setViewerState(AppObject.EmptyViewerState);
        setEditorState({open: true, item: item});
    };

    // Auto 우선순위 그룹 추가 요청
    const onRequestAddAutoGroup = (item) => {
        setAutoPlanOrderArriveInfo({variables:{arriveInfo:{arriveId:item.arriveId, autoPlanOrder:item.autoPlanOrder}}});
    };

    // Auto 우선순위 그룹 제외 요청
    const onRequestRemoveAutoGroup = (item) => {
        unsetAutoPlanOrderArriveInfo({variables:{arriveInfo:{arriveId:item.arriveId}}});
    };

    // 자동배차 처리 표시 순서 변경 요청
    const onRequestMove = (param) => {
        setAutoViewOrderArriveInfo({variables:{arriveInfo:param}});
    };

    const confirmStopUsing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "출발도착정보 사용중지",
            content: [
                `${item.dcName}-${item.storeName}간 출발도착정보를 사용중지하시겠습니까?`,
            ]
        });

        if(confirm)
            setUsageArriveInfo({variables:{arriveInfo:{arriveId:item.arriveId, useYn:'N'}}});
    };

    const confirmReusing = async (item) => {
        const confirm = await openConfirmDialog({
            title: "출발도착정보 사용",
            content: [
                `${item.dcName}-${item.storeName}간 출발도착정보를 사용하도록 변경하시겠습니까?`,
            ]
        });
        if(confirm) setUsageArriveInfo({variables:{arriveInfo:{arriveId:item.arriveId, useYn:'Y'}}});
    };

    const onClickOpenFreightPopup = (item) => {
        setChargeInfoState({open:true, item:item});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case AppObject.command.STOP_USING: confirmStopUsing(row); break;
            case AppObject.command.REUSE: confirmReusing(row); break;
            case AppObject.command.VIEW: onDoubleClickRow(row); break;
            case AppObject.command.TARIFF: onClickOpenFreightPopup(row); break;
            default: return;
        }
    };
    /*
	CALL wps_set_auth_table('pr_arrive_info_add', '1,3', v_msg); -- 도착지 (not store)
	CALL wps_set_auth_table('pr_arrive_info_edit', '1,3', v_msg);
	CALL wps_set_auth_table('pr_arrive_info_remove', '1,3', v_msg);
	CALL wps_set_auth_table('pr_arrive_info_set_auto_plan_order', '1,3', v_msg);
	CALL wps_set_auth_table('pr_arrive_info_set_usage', '1,3', v_msg);
	CALL wps_set_auth_table('pr_arrive_info_unset_auto_plan_order', '1,3', v_msg);
	CALL wps_set_auth_table('pr_arrive_info_set_auto_view_order', '1,3', v_msg);
	CALL wps_set_auth_table('pr_arrive_info_upload', '1,3', v_msg);
    */

    const canAdd = checkAuth(authTableTree.tree.pr_arrive_info_add);
    const canEdit = checkAuth(authTableTree.tree.pr_arrive_info_edit);
    const canSetUsage = checkAuth(authTableTree.tree.pr_arrive_info_set_usage);
    const canUpload = checkAuth(authTableTree.tree.pr_arrive_info_upload);
    const canRemove = checkAuth(authTableTree.tree.pr_arrive_info_remove);
    const canSetAutoPlanOrder = checkAuth(authTableTree.tree.pr_arrive_info_set_auto_plan_order);
    const canUnsetAutoPlanOrder = checkAuth(authTableTree.tree.pr_arrive_info_unset_auto_plan_order);
    const canSetAutoViewOrder = checkAuth(authTableTree.tree.pr_arrive_info_set_auto_view_order);
    const canAddArriveChargeInfo = checkAuth(authTableTree.tree.pr_arrive_charge_info_add);

    const tools = [
        {id:AppObject.command.VIEW, icon:<PageviewIcon/> }
    ];

    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canSetUsage) {
        tools.push({id:AppObject.command.STOP_USING, icon:<StopCircleIcon/>, color: AppStyle.mui.error, where:(row)=>row.useYn === "Y"});
        tools.push({id:AppObject.command.REUSE, icon:<PlayCircleFilledWhiteIcon/>, color: AppStyle.mui.success, where:(row)=>row.useYn !== "Y"});
    }
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    if(canAddArriveChargeInfo) tools.push({id:AppObject.command.TARIFF, icon:<PaidIcon/>, color: AppStyle.mui.primary, where:(row)=>row.useYn === "Y"});

    const loadingAny
    = responseList.loading
    || responseRemove.loading
    || responseEdit.loading
    || responseAdd.loading
    || responseUsage.loading
    || responseSetAutoPlanOrder.loading
    || responseUnsetAutoPlanOrder.loading
    || responseSetAutoViewOrder.loading
    || responseDcList.loading
    || responseItem.loading;

    const renderUploadPop = () => {
        return(
            <Popover
                open={Boolean(uploadPopAnchorEl)}
                anchorEl={uploadPopAnchorEl}
                onClose={()=>setUploadPopAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box style={{padding:10, maxWidth:300}}>
                    <Box style={{
                        marginBottom:10, padding:10, borderBottom:AppStyle.BorderCCC, borderRadius:5,
                        backgroundColor: AppStyle.PrimaryRGB, color:'white'
                    }}>
                        파일 업로드하기
                    </Box>
                    <p style={{fontSize:'0.9rem'}}>
                        출발도착정보 일괄 등록을 위해 CSV 파일을 준비해 주세요.<br/>
                        파일이 CSV 파일로 저장될 때 일반적으로는 UTF-8 인코딩이 사용됩니다.
                        하지만, 오래전에 작성된 파일이나 저장한 프로그램에 따라 EUC-KR 인코딩(완성형)이
                        사용된 경우도 있습니다. 이 경우 아래 체크박스를 선택하세요.<br/>
                        잘 모르시면, 일단 열어본 후 한글이 깨지면 체크 상태를 바꾸고
                        다시 열어보세요.
                    </p>
                    <Box style={{borderTop:'1px solid #ccc'}}>
                        <VzmCheckbox
                            dict={{
                                id: 'useEucKr', label: '업로드 파일 완성형한글로 읽기',
                            }}
                            value={useEucKr}
                            onChange={(id,value,err) => setUseEucKr(value)}
                        />
                    </Box>
                    <Button
                        fullWidth
                        variant={AppStyle.mui.contained}
                        onClick={onClickUpload}
                        startIcon={<CloudUploadIcon/>}
                    >
                        출발도착정보 일괄 등록
                    </Button>
                </Box>
            </Popover>
        );
    };

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <Box style={{marginRight:20}}>
                        <VzmSelector
                            dict={{
                                id: 'dcCd',
                                valueKey: 'dcCd',
                                labelKey: 'dcName',
                            }}
                            value={dcSelected.dcCd}
                            onChange={(id, value)=>{return;}}
                            onSelectRecord={(record)=>dcSelectedRepo(record)}
                            list={dcInfoListData.records}
                        />
                        &nbsp;&nbsp;
                        <VzmSelector
                            dict={{id:'usage', valueKey:'value', label: '사용여부'}}
                            value={filterUsage}
                            onChange={(id, value)=>setFilterUsage(value)}
                            list={UsageFilterSelect}
                        />
                        &nbsp;&nbsp;
                        <VzmSelector
                            dict={{id:'apy', valueKey:'value', label: '자동배차 사용구분', minWidth:140}}
                            value={filterAutoPlan}
                            onChange={(id, value)=>setFilterAutoPlan(value)}
                            list={AutoPlanFilterSelect}
                        />
                    </Box>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny
                        ? <CircleLoading /> :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                            title='새로 고침'
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                            title='출발도착정보 신규 등록'
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                    <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={onChooseFile}
                    />
                    {
                        fileInputRef.current && canUpload &&
                        <IconButton
                            color={AppStyle.mui.standard}
                            onClick={(event)=>setUploadPopAnchorEl(event.currentTarget)}
                            title='출발도착정보 일괄 등록'
                            style={{marginLeft:10}}
                        >
                            <CloudUploadIcon />
                        </IconButton>
                    }
                    {fileInputRef.current && canUpload && renderUploadPop()}
                    <Button
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                        startIcon={<StoreIcon/>}
                        onClick={()=>setOpenStoreList(true)}
                        size={AppWord.SMALL}
                        style={{marginLeft:10}}
                    >
                        점포목록
                    </Button>
                    {
                        (canSetAutoPlanOrder || canUnsetAutoPlanOrder) &&
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.secondary}
                            startIcon={<AutoFixHighIcon/>}
                            onClick={onClickOpenAutoOrderGroupEditor}
                            size={AppWord.SMALL}
                            style={{marginLeft:10}}
                            disabled={!dcSelected.dcCd}
                        >
                            AUTO 우선순위 그룹
                        </Button>
                    }
                    {
                        canSetAutoViewOrder &&
                        <Button
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.success}
                            startIcon={<AutoAwesomeMotionIcon/>}
                            onClick={onClickOpenAutoOrderSetter}
                            size={AppWord.SMALL}
                            style={{marginLeft:10}}
                            disabled={!dcSelected.dcCd}
                        >
                            자동배차 처리 표시
                        </Button>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={ArriveInfoDictionary.tableProps.tableId}
                tableProps={ArriveInfoDictionary.tableProps}
                records={arriveListForDc}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
            />
            <ArriveInfoView
                open={viewerState.open}
                item={viewerState.item}
                onRequestEdit={onRequestEditFromViewer}
                onClose={onCloseView}
            />
            <ArriveInfoInput
                open={editorState.open}
                item={editorState.item}
                currentDcInfo={dcSelected}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
            <StoreInfo open={openStoreList} onClose={()=>setOpenStoreList(false)} />
            <ArriveAutoGroup
                open={autoOrderGroupEditorState.open}
                dcSelected={dcSelected}
                planGroup={autoOrderGroupEditorState.planGroup}
                onClose={()=>setAutoOrderGroupEditorState({open:false, planGroup:[]})}
                onRequestAdd={onRequestAddAutoGroup}
                onRequestRemove={onRequestRemoveAutoGroup}
                responseAdd={responseSetAutoPlanOrder}
                responseRemove={responseUnsetAutoPlanOrder}
            />
            <ArriveAutoViewOrder
                open={autoOrderControlOpen}
                dcSelected={dcSelected}
                onClose={()=>setAutoOrderControlOpen(false)}
                onRequestMove={onRequestMove}
                response={responseSetAutoViewOrder}
            />
            <ArriveInfoUploader open={uploadViewState.open}
                csvText={uploadViewState.csvText}
                onClose={onCloseUploader}
            />
            <ArriveChargeInfo
                open={chargeInfoState.open}
                arriveInfoGiven={chargeInfoState.item}
                onRequestClose={()=>setChargeInfoState({open: false, item: null})}
            />
        </ContentConainer>
    );
}
